// Free-form text (a chat note) can contain links and email addresses the user typed. There is no
// link entity on a note and no unfurl service, so both the Links list in the notes media view and
// the clickable links inside a chat bubble are derived from the note's own text — by this module, so
// the two surfaces always agree on what counts as a link and where it starts and ends.

// An explicit http(s) URL, or a bare `www.` host. Trailing sentence punctuation is trimmed off
// afterwards so "see www.g2.com." doesn't pull the full stop into the href. Whitespace, angle
// brackets and straight quotes end a match — and so do the curly quotes (U+2018 to U+201F) a smart
// keyboard or pasted rich text substitutes for them, or "https://example.com’s policy" would put the
// `’s` in the href and "“https://example.com”" would keep the closing quote, while the
// straight-quote spellings of both already end cleanly. A raw curly quote is never a legal URL
// character; one that is genuinely part of a link arrives percent-encoded.
//
// The first alternative is an email address: a local part that starts with a letter or digit, then
// a dotted domain ending in a TLD-looking label. Neither half admits sentence punctuation at its
// end, so "email a@b.com." needs no trimming. The lookahead refuses to stop partway through a
// domain, so "bob@example.com2" is not linked as "bob@example.com". The scan takes the leftmost
// match, so an `@` inside a URL ("https://x.com/?to=a@b.com") stays part of that URL, and an
// address whose domain starts with `www.` ("bob@www.example.com") is matched as the address rather
// than half of it as a web link. Group 1 is an email address, group 2 a URL candidate.
const LINK_PATTERN =
	/([a-z0-9][a-z0-9._%+'-]*@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}(?![\p{L}\p{N}-]|\.[\p{L}\p{N}]))|(https?:\/\/[^\s<>"'\u2018-\u201F]+|www\.[^\s<>"'\u2018-\u201F]+)/giu;

// Two links typed back to back with only a separator between them ("https://a.com,https://b.com")
// come back as a single match, because the URL alternative runs to the next whitespace. A `,` or `;`
// that is immediately followed by another URL start is therefore the end of THIS link, not part of
// it.
const SEPARATOR_BEFORE_NEXT_URL = /[,;](?=https?:\/\/|www\.)/i;

// The same goes for email addresses typed straight after a link ("https://a.com,jo@x.com,ann@y.com"),
// but only when the rest of the match is nothing but such addresses and the link before them is a
// bare host. "https://x.com/contacts/a,jo@x.com" and "https://x.com/?to=a,jo@x.com" are each one
// URL that happens to hold an address.
const SEPARATOR_BEFORE_EMAIL =
	/[,;](?=[a-z0-9][a-z0-9._%+'-]*@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}(?:[,;][a-z0-9][a-z0-9._%+'-]*@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,})*[.:!?)\]}]*$)/i;
const LINK_WITH_PATH = /^(?:https?:\/\/|www\.)[^/?#]*[/?#]/i;

// A letter, digit, or local-part character sitting right before an email match means the typed
// address is longer than the match ("o’connor@x.com", "éjo@x.com" or "ops!night@x.com" would
// otherwise link as "connor@x.com", "jo@x.com" or "night@x.com"), so the whole thing stays plain
// text rather than mailing someone else. Wrappers and delimiters such as `*`, `` ` ``, `=` and an opening `'` are left out, so
// "*jo@x.com*" and "email=jo@x.com" still link.
const EMAIL_CHARACTER_BEFORE_MATCH = /[\p{L}\p{N}._%+!#$&^~\u2019@-]/u;

// Cut a match short at the separator that starts the next link, leaving that link for the next pass.
const truncateAtNextLink = (candidate: string): string => {
	const urlSeparatorIndex = candidate.search(SEPARATOR_BEFORE_NEXT_URL);
	const link = urlSeparatorIndex === -1 ? candidate : candidate.slice(0, urlSeparatorIndex);
	const emailSeparatorIndex = link.search(SEPARATOR_BEFORE_EMAIL);
	if (emailSeparatorIndex === -1 || LINK_WITH_PATH.test(link.slice(0, emailSeparatorIndex))) {
		return link;
	}
	return link.slice(0, emailSeparatorIndex);
};

// Characters that end the sentence rather than the URL. `<`, `>`, `"`, `'` and the curly quotes are
// absent on purpose: the URL alternative already excludes those, so they can never end a match.
const TRAILING_SENTENCE_PUNCTUATION = '.,;:!?';

// Markdown marks get into a match because the URL alternative does not exclude them, so
// "`https://x.com`" pulls the closing backtick in — but every one of them is also a legal URL
// character, and a path or a base64url token can genuinely end in `_` or `~`. So they are only trimmed when the SAME mark
// sits immediately before the match, which is the wrapper's opening half: the same "the opener is
// outside the match" tell the bracket rule below uses.
const MARKDOWN_WRAPPER_MARKS = '`*~_';

// A closing bracket is only sentence punctuation when the URL never opened one — compare
// "(see https://example.com/a)", where the `)` closes the aside, with
// "https://en.wikipedia.org/wiki/Foo_(bar)", where it belongs to the path. The opening bracket of an
// aside sits outside the match (a URL match starts at `https`/`www.`), so an unmatched closer is the
// tell.
const OPENING_BRACKET_BY_CLOSING: { [closingBracket: string]: string } = {
	')': '(',
	']': '[',
	'}': '{',
};

const countCharacter = (text: string, character: string): number =>
	text.split(character).length - 1;

// Peel sentence punctuation off the end of a matched URL, leaving brackets that the URL balances and
// Markdown marks that nothing opened. `precedingCharacter` is the character immediately before the
// match in the source text, or '' when the match starts the text.
const trimTrailingPunctuation = (candidate: string, precedingCharacter: string): string => {
	let trimmed = candidate;

	while (trimmed.length > 0) {
		const lastCharacter = trimmed.charAt(trimmed.length - 1);
		const openingBracket = OPENING_BRACKET_BY_CLOSING[lastCharacter];

		if (openingBracket) {
			if (countCharacter(trimmed, lastCharacter) <= countCharacter(trimmed, openingBracket)) {
				break;
			}
		} else if (MARKDOWN_WRAPPER_MARKS.indexOf(lastCharacter) !== -1) {
			if (lastCharacter !== precedingCharacter) {
				break;
			}
		} else if (TRAILING_SENTENCE_PUNCTUATION.indexOf(lastCharacter) === -1) {
			break;
		}

		trimmed = trimmed.slice(0, -1);
	}

	return trimmed;
};

// A candidate that already declared a scheme is a link on that basis alone — an explicit
// "http://192.168.1.20/status" or "http://localhost:3000" is a place someone meant to send you, and
// has no TLD to show for it — it does still have to name a host, so a bare "http://" left behind by
// trimming an ellipsis is not one. A bare "www." candidate has only its shape to go on, so it still
// has to carry a TLD-looking suffix, which keeps things like "3.5" or a lone "www." out of the list.
const isLinkLike = (candidate: string): boolean =>
	/^https?:\/\/[^\s/?#]/i.test(candidate) ||
	(/^www\./i.test(candidate) && /\.[a-z]{2,}/i.test(candidate));

export interface LinkTextSegment {
	/** The text exactly as it should read on screen. */
	value: string;
	/** Present only on link segments — the href to open. */
	href?: string;
	/** Set on link segments that are email addresses, whose href is a `mailto:`. */
	isEmail?: boolean;
}

/** A scheme-less link ("www.g2.com") still needs one before it can be used as an href. */
export const normalizeLinkHref = (url: string): string =>
	/^https?:\/\//i.test(url) ? url : `https://${url}`;

/** Host of a link, `www.` included, the way the design labels a link row. */
export const getLinkHostname = (url: string): string => {
	try {
		return new URL(normalizeLinkHref(url)).hostname;
	} catch (error) {
		return url;
	}
};

/**
 * Best-effort name for a link that has no surrounding text to borrow from: the last path segment,
 * de-slugified ("/product-launch-2026" → "product launch 2026"), else the host.
 */
export const getLinkFallbackTitle = (url: string): string => {
	try {
		const parsed = new URL(normalizeLinkHref(url));
		const pathSegments = parsed.pathname.split('/').filter(Boolean);
		const lastSegment = pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : '';
		if (lastSegment) {
			const readable = decodeURIComponent(lastSegment)
				.replace(/\.[a-z0-9]{1,5}$/i, '')
				.replace(/[-_+]+/g, ' ')
				.trim();
			if (readable) {
				return readable;
			}
		}
		return parsed.hostname;
	} catch (error) {
		return url;
	}
};

/**
 * Split text into alternating plain and link segments, in order. Text with no links comes back as
 * a single plain segment, and empty text as an empty list.
 */
export const splitTextByLinks = (text: string): LinkTextSegment[] => {
	if (!text) {
		return [];
	}

	const segments: LinkTextSegment[] = [];
	// Tracks where the next plain segment starts. Whatever a match gave back — trailing punctuation,
	// or the next link — belongs to the text after the link that was emitted.
	let plainTextStart = 0;

	LINK_PATTERN.lastIndex = 0;
	let match = LINK_PATTERN.exec(text);
	while (match !== null) {
		if (match[1]) {
			if (EMAIL_CHARACTER_BEFORE_MATCH.test(text.charAt(match.index - 1))) {
				match = LINK_PATTERN.exec(text);
				continue;
			}
			if (match.index > plainTextStart) {
				segments.push({ value: text.slice(plainTextStart, match.index) });
			}
			// A `%` in the local part is literal, but a mailto URI would decode "%2B" into "+".
			const href = `mailto:${match[1].replace(/%/g, '%25')}`;
			segments.push({ value: match[1], href, isEmail: true });
			plainTextStart = match.index + match[1].length;
			match = LINK_PATTERN.exec(text);
			continue;
		}

		const candidate = trimTrailingPunctuation(
			truncateAtNextLink(match[0]),
			text.charAt(match.index - 1)
		);
		if (isLinkLike(candidate)) {
			if (match.index > plainTextStart) {
				segments.push({ value: text.slice(plainTextStart, match.index) });
			}
			segments.push({ value: candidate, href: normalizeLinkHref(candidate) });
			plainTextStart = match.index + candidate.length;
		}
		// Resume the scan immediately after whatever this match kept, not after the whole match: what
		// it gave back can itself hold the next link. This applies even when the kept part was not a
		// link ("www.,https://a.com" keeps a bare "www.", and the real link is in the tail). Guarded
		// on a non-empty candidate so the cursor always moves forward.
		if (candidate.length > 0) {
			LINK_PATTERN.lastIndex = match.index + candidate.length;
		}
		match = LINK_PATTERN.exec(text);
	}

	if (plainTextStart < text.length) {
		segments.push({ value: text.slice(plainTextStart) });
	}

	return segments;
};
